import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { addDays, format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { usePost } from './../../apis/ApiRequests';
import { dateToString } from '../../utils/Library';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, Box, Typography, Button } from '@mui/material';
import { facilityData } from '../../Store';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

const TankListMonitor = () => {

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - セッション情報を取得
	//-----------------------------------------------
    const { authInfo }   = useAuth();
    const facilityCode   = authInfo.facilityCode ? authInfo.facilityCode : facilityData.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId ? authInfo.userId : 'Signage';
    const luserName      = authInfo.userName;
    const authority      = authInfo.authority;
    const today          = format(addDays(new Date(), 0), 'yyyy-MM-dd');
    const navigate       = useNavigate();
    
    const search    = useLocation().search;
    const query     = new URLSearchParams(search);
    const tankType = query.get('tankType');

	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ entryDate, setEntryDate] = useState(today);
    const [ tankList,   setTankList  ] = useState([]);
    
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
    // API - searchTankDataByUnload / Signage
	//-----------------------------------------------
    const { response : searchTankData_res, post:searchTankData_post } = usePost();
    const searchTankData = async () => {
        const numericDate = dateToString(entryDate);
        const postData = {
            'className'    : 'mch.models.table.TankList',
            'methodName'   : 'getCurrentTank',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'entryDate'    : numericDate,
            'tankType'     : tankType,
        }
        await searchTankData_post(postData);
    };
	//-----------------------------------------------
    // API - resetTankList / Signage
	//-----------------------------------------------
    const { response : resetTankList_res, post:resetTankList_post } = usePost();
    const resetTankList = async (tank) => {
        const numericDate = dateToString(entryDate);
        const postData = {
            'className'    : 'mch.models.table.TankList',
            'methodName'   : 'resetTankList',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'entryDate'    : numericDate,
            'tankCode'     : tank.tankCode,
            'tankSeq'      : tank.tankSeq,
        }
        await resetTankList_post(postData);
    };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        searchTankData();
        
        // 30秒ごとにデータを更新するタイマーを設定
        const interval = setInterval(searchTankData, 5000);
    
        // コンポーネントのアンマウント時にタイマーをクリア
        return () => clearInterval(interval);
    },[]);
	//-----------------------------------------------
    // useEffect - searchTankDataByUnload / Signage
	//-----------------------------------------------
    useEffect(() => {
        setTankList([]);
        if (searchTankData_res) {
            setTankList(searchTankData_res.Data);
        }
    }, [searchTankData_res]);
	//===============================================
	// eventHandler
	//===============================================
    const handleClick_header = () => {
        userId == 'Signage' ? navigate('/') : navigate('/signageMenu');
    };
	//-----------------------------------------------
	// eventHandler - ResetTank
	//-----------------------------------------------
    const handleClick_ResetTank = async(tank) => {
        await resetTankList(tank);
        searchTankData();
    };
    //-----------------------------------------------
    // eventHandler - ResetAllTank
    //-----------------------------------------------
    const handleClick_ResetAllTank = async () => {
        await Promise.all(
            tankList.map(tank => resetTankList(tank))
        );
        searchTankData();
    };
    //-----------------------------------------------
    // Function - 小数単位の統一
    //-----------------------------------------------
    const formatKg = (value) => {
        if (value === null || value === undefined || value === '') return '';
        return Number(value).toFixed(2);
    };
    //-----------------------------------------------
    // Function - 容量によって色分け（背景＋文字）
    //-----------------------------------------------
    const getTankStyle = (tank) => {
        if (!tank.limitSuryo || tank.limitSuryo === 0) {
            return {
                backgroundColor: '#005243',
                color: '#ffffff',
                titleColor: '#FFBF00',
            };
        }

        // 80%以上：赤
        if (tank.displayStatus == 'red') {
            return {
                backgroundColor: '#ff0000',
                color: '#ffffff',
            };
        }

        // 70%以上：黄色
        if (tank.displayStatus == 'yellow') {
            return {
                backgroundColor: '#FFBF00',
                color: '#005243',
            };
        }

        // 通常
        return {
            backgroundColor: '#005243',
            color: '#ffffff',
        };
    };
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <div className='bidGroup signageContens' style={{ height: '100vh', overflow: 'hidden',  padding: '10px' }}>
                <div style={{ textAlign: 'center' }} onClick={() => handleClick_header()}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            height: 56,          // AppBarっぽい高さ
                            px: 2,
                        }}
                    >
                    <Typography
                        variant="h6"
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                            <span style={{ fontSize: '1.7em', fontWeight: 'bold', color: '#fff' }}>
                                {Number(tankType) === 1 ? '【活魚水槽情報】' : '【鮮魚水槽情報】'}
                            </span>
                            <span
                                style={{
                                    fontSize: '1.7em',
                                    fontWeight: 'bold',
                                    color: '#fff',
                                    marginLeft: '12px',
                                }}
                            >
                                {today}
                            </span>
                    </Typography>
                    {/* 右端ボタン */}
                    <Box sx={{ marginLeft: 'auto' }}>
                        <Box
                            sx={{
                                border: '1px solid #ffffff',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                backgroundColor: '#005243',
                                color: '#ffffff',
                                width: '75px',
                                padding: '4px 12px',
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                            }}
                            
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClick_ResetAllTank();
                            }}
                        >
                            <RestartAltIcon />
                        </Box>
                    </Box>
                    </Box>
                </div>
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: '10px',
                                    padding: 5,

                    }}
                >
                    {tankList.map((tank, idx) => {
                        const tankStyle = getTankStyle(tank);
                        return (
                            <Box
                                key={idx}
                                sx={{
                                    border: '1px solid #ffffff',
                                    borderRadius: '10px',
                                    cursor: 'pointer',
                                    backgroundColor: tankStyle.backgroundColor,
                                    color: tankStyle.color,
                                    height: 'clamp(90px, 17vh, 190px)',
                                    boxSizing: 'border-box',
                                    overflow: 'hidden',
                                    padding: 0,
                                }}
                                onClick={() => handleClick_ResetTank(tank)}
                            >
                                {/* ===== タイトル帯（暗い板） ===== */}
                                <Box
                                    sx={{
                                        backgroundColor: '#fff',
                                        padding: '0.1em',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#005243',
                                            textAlign: 'center',
                                            fontSize: Number(tankType) === 0 ? '1.7em' : '1.3em',
                                        }}
                                    >
                                        {tank.displayName}{' '}{formatKg(tank.totalSuryo)}Kg
                                    </Typography>
                                </Box>

                                {/* ===== 明細 ===== */}
                                <Box
                                    sx={{
                                        padding: '5px',
                                    }}
                                >
                                    {tank.bidList.slice(0, 3).map((bid, i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                textAlign: 'center',
                                                paddingLeft: '10px',
                                                fontSize: Number(tankType) === 0 ? '1.7em' : '1.3em',
                                                fontWeight: 'bold',
                                                listStylePosition: 'inside',
                                            }}
                                        >
                                            {bid.gyoshu}{' / '}{formatKg(bid.kgSuryo)}{'Kg'}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </div>
        </>
    )
}
export default TankListMonitor;